import { $isListNode, $isListItemNode, $createListNode, $createListItemNode } from '@lexical/list';
import { LexicalEditor, $getRoot, $createParagraphNode, $createTextNode, TextNode, $isElementNode, LexicalNode } from 'lexical';
import {
  $isWhatsAppBoldNode,
  $isWhatsAppItalicNode,
  $isWhatsAppStrikethroughNode,
  $isWhatsAppMonospaceNode,
  $isWhatsAppCodeBlockNode,
  $createWhatsAppBoldNode,
  $createWhatsAppItalicNode,
  $createWhatsAppStrikethroughNode,
  $createWhatsAppMonospaceNode,
  $createWhatsAppCodeBlockNode,
  $isMentionNode,
  $createMentionNode,
} from '../nodes';
import { $isQuoteNode } from '@lexical/rich-text';
import { $createWhatsAppQuoteNode, $isWhatsAppQuoteNode } from '../nodes';

/**
 * Pattern for an @mention serialised into markdown:
 *   `@[Display Name](972526847417@c.us)`
 *
 * Matches what the WhatsApp send-time pipeline expects in
 * `bt-common prepareSend` (extension/wa-communication-layer/
 * message/message-utils.ts) — that helper splits the JIDs into
 * `mentionedJidList` and rewrites the body to `@<phone>`, which is
 * what triggers the actual mention notification on the recipient's
 * device.
 *
 * The display group accepts anything except `]`; the id group accepts
 * anything except `)` and intentionally requires at least one `@` so
 * a stray `@[x](y)` literal in user prose isn't mistaken for a mention.
 */
const MENTION_PATTERN = /@\[([^\]\n\r]+)\]\(([^)\n\r]*@[^)\n\r]+)\)/;

/**
 * WhatsApp emphasis markers, in the nesting order used when two of them cover exactly
 * the same run (outermost first): bold+italic on one run serializes as `_*text*_`.
 */
const EMPHASIS_MARKERS = ['~', '_', '*'] as const;
type EmphasisMarker = typeof EMPHASIS_MARKERS[number];

/** One inline piece of a line: its literal markdown text and the emphasis over it. */
interface InlineSegment {
  text: string;
  marks: ReadonlySet<EmphasisMarker>;
}

const NO_MARKS: ReadonlySet<EmphasisMarker> = new Set();

const collectInlineSegments = (node: LexicalNode, out: InlineSegment[]): void => {
  // MentionNode check FIRST — it's a TextNode subclass, so the generic `text` branch
  // below would otherwise eat it and strip the `@[name](id)` wrapper.
  if ($isMentionNode(node)) {
    out.push({ text: `@[${node.getDisplay()}](${node.getId()})`, marks: NO_MARKS });
  } else if ($isWhatsAppBoldNode(node)) {
    out.push({ text: node.getTextContent(), marks: new Set(['*']) });
  } else if ($isWhatsAppItalicNode(node)) {
    out.push({ text: node.getTextContent(), marks: new Set(['_']) });
  } else if ($isWhatsAppStrikethroughNode(node)) {
    out.push({ text: node.getTextContent(), marks: new Set(['~']) });
  } else if ($isWhatsAppMonospaceNode(node)) {
    out.push({ text: `\`${node.getTextContent()}\``, marks: NO_MARKS });
  } else if (node.getType() === 'linebreak') {
    // FE#277: Shift+Enter inserts a soft LineBreakNode INSIDE the paragraph. Emit a
    // real newline so it survives the send, like Enter (a new paragraph) does.
    out.push({ text: '\n', marks: NO_MARKS });
  } else if (node.getType() === 'text') {
    // Standard Lexical format flags — what the parser produces and what Ctrl+B/I set.
    const textNode = node as TextNode;
    const marks = new Set<EmphasisMarker>();
    if (textNode.hasFormat('bold')) marks.add('*');
    if (textNode.hasFormat('italic')) marks.add('_');
    if (textNode.hasFormat('strikethrough')) marks.add('~');
    const text = textNode.getTextContent();
    // Inline code is literal: emphasis goes OUTSIDE the backticks (`*`x`*`), since
    // markers inside them would print as characters.
    out.push({ text: textNode.hasFormat('code') ? `\`${text}\`` : text, marks });
  } else if ($isElementNode(node)) {
    // e.g. the paragraph a quote wraps its line in.
    node.getChildren().forEach((child) => collectInlineSegments(child, out));
  } else {
    out.push({ text: node.getTextContent(), marks: NO_MARKS });
  }
};

/**
 * Serialize one line of inline nodes to WhatsApp markdown.
 *
 * Emphasis is emitted per RUN, not per node: adjacent nodes sharing a format share one
 * marker pair, so nested formatting round-trips — `*bold _both_*` parses to
 * [bold "bold ", bold+italic "both"] and serializes back to `*bold _both_*`, not
 * `*bold* _*both*_`. The marker whose run lasts longest opens first (outermost); a run
 * that would cross an enclosing one is closed and reopened, so markers always nest.
 *
 * Leading/trailing whitespace of a run goes OUTSIDE its markers: WhatsApp renders a
 * marker adjacent to whitespace — `*text *` or `* text*` — as LITERAL asterisks
 * (8395e5282). A whitespace-only run gets no markers at all.
 */
const serializeInline = (nodes: LexicalNode[]): string => {
  const segments: InlineSegment[] = [];
  nodes.forEach((node) => collectInlineSegments(node, segments));
  const plain = segments.map((segment) => segment.text).join('');

  const runLength = (from: number, marker: EmphasisMarker): number => {
    let length = 0;
    while (from + length < segments.length && segments[from + length].marks.has(marker)) length++;
    return length;
  };

  // 1. Properly nested emphasis spans over `plain`, in opening order.
  const spans: { marker: EmphasisMarker; start: number; end: number }[] = [];
  const open: typeof spans = [];
  let offset = 0;
  for (let i = 0; i <= segments.length; i++) {
    const marks = i < segments.length ? segments[i].marks : NO_MARKS;
    const firstEnded = open.findIndex((span) => !marks.has(span.marker));
    if (firstEnded >= 0) open.splice(firstEnded).forEach((span) => { span.end = offset; });
    EMPHASIS_MARKERS
      .filter((marker) => marks.has(marker) && !open.some((span) => span.marker === marker))
      .sort((a, b) => runLength(i, b) - runLength(i, a)) // stable: ties keep EMPHASIS_MARKERS order
      .forEach((marker) => {
        const span = { marker, start: offset, end: offset };
        open.push(span);
        spans.push(span);
      });
    if (i < segments.length) offset += segments[i].text.length;
  }

  // 2. Move whitespace out of each span, then place the markers. Trimming keeps the
  //    nesting (an inner span's text lies inside its outer span's), and at one position
  //    closes come before opens: inner-first closing, outer-first opening.
  const inserts: { at: number; marker: EmphasisMarker; rank: number }[] = [];
  spans.forEach((span, seq) => {
    let { start, end } = span;
    while (start < end && /\s/.test(plain[start])) start++;
    while (end > start && /\s/.test(plain[end - 1])) end--;
    if (start === end) return;
    inserts.push({ at: start, marker: span.marker, rank: seq }, { at: end, marker: span.marker, rank: -1 - seq });
  });
  inserts.sort((a, b) => a.at - b.at || a.rank - b.rank);

  let line = '';
  let cursor = 0;
  inserts.forEach(({ at, marker }) => {
    line += plain.slice(cursor, at) + marker;
    cursor = at;
  });
  return line + plain.slice(cursor);
};

/**
 * Serialize Lexical editor content to WhatsApp markdown format
 * 
 * @param editor - Lexical editor instance
 * @returns WhatsApp markdown string
 */
export const serializeEditorContent = (editor: LexicalEditor): string => {
  let markdown = '';
  
  editor.getEditorState().read(() => {
    const root = $getRoot();
    const children = root.getChildren();
    
    children.forEach((node, index) => {
      if ($isElementNode(node) && node.getType() === 'paragraph') {
        markdown += serializeInline(node.getChildren());
        if (index < children.length - 1) {
          markdown += '\n';
        }

      } else if ($isListNode(node)) {
        // Handle List Node
        const listChildren = node.getChildren();
        const listType = node.getTag(); // 'ul' or 'ol'
        
        listChildren.forEach((listItem, listItemIndex) => {
            if ($isListItemNode(listItem)) {
                const itemText = serializeInline(listItem.getChildren());

                const prefix = listType === 'ul' ? '• ' : `${listItemIndex + 1}. `;
                markdown += `${prefix}${itemText}`;
                
                // Add newline unless it's the very last item of the list AND the list is the last child
                if (listItemIndex < listChildren.length - 1 || index < children.length - 1) {
                    markdown += '\n';
                }
            }
        });

  } else if ($isWhatsAppCodeBlockNode(node)) {
        const codeContent = node.getTextContent();
        markdown += `\`\`\`\n${codeContent}\n\`\`\``;
        if (index < children.length - 1) {
          markdown += '\n';
        }
      } else if ($isQuoteNode(node) || $isWhatsAppQuoteNode(node)) {
        // Each QuoteNode represents a single line (WhatsApp style)
        // Serialize quote children to preserve formatting
        markdown += `> ${serializeInline(node.getChildren())}`;
        
        if (index < children.length - 1) {
          markdown += '\n';
        }
      }
    });
  });
  
  return markdown;
};

/**
 * Generate Lexical Nodes from WhatsApp markdown string.
 * This effectively parses the markdown into a list of nodes ready to be inserted.
 */
export const $generateNodesFromMarkdown = (markdown: string): LexicalNode[] => {
    const nodes: LexicalNode[] = [];
    if (!markdown) {
        return [$createParagraphNode()];
    }

    const lines = markdown.split('\n');
    let inCodeBlock = false;
    let codeBlockContent = '';

    let i = 0;
    while (i < lines.length) {
        const line = lines[i];

        // Handle Code Blocks
        if (line.trim() === '```') {
            if (inCodeBlock) {
                // End of code block
                const codeNode = $createWhatsAppCodeBlockNode();
                codeNode.append($createTextNode(codeBlockContent.trim()));
                nodes.push(codeNode);
                inCodeBlock = false;
                codeBlockContent = '';
            } else {
                // Start of code block
                inCodeBlock = true;
            }
            i++;
            continue;
        }

        // Handle Lists (Bullets and Numbers)
        const bulletMatch = line.match(/^[-*•]\s+(.+)/);
        const numberMatch = line.match(/^(\d+)\.\s+(.+)/);

        if ((bulletMatch || numberMatch) && !inCodeBlock) {
            const listType = bulletMatch ? 'bullet' : 'number';
            const listNode = $createListNode(listType);

            // We need to consume this line and subsequent lines that are also lists of same type
            let j = i;
            while (j < lines.length) {
                const innerLine = lines[j];
                const innerBullet = innerLine.match(/^[-*•]\s+(.+)/);
                const innerNumber = innerLine.match(/^(\d+)\.\s+(.+)/);

                let itemText = null;

                if (listType === 'bullet' && innerBullet) {
                    itemText = innerBullet[1];
                } else if (listType === 'number' && innerNumber) {
                    itemText = innerNumber[2];
                } else {
                    // End of list
                    break;
                }

                const listItem = $createListItemNode();
                const inlineTextNodes = parseInlineFormatting(itemText);
                inlineTextNodes.forEach(node => listItem.append(node));
                listNode.append(listItem);
                j++;
            }

            nodes.push(listNode);
            i = j; // Update outer index
            continue;
        }

        if (inCodeBlock) {
            codeBlockContent += line + '\n';
            i++;
            continue;
        }
        // Handle Blockquotes - WhatsApp creates one blockquote per line
        if (line.trim().startsWith('>')) {
            // Remove the > prefix
            let content = line.trim().substring(1);
            if (content.startsWith(' ')) {
                content = content.substring(1);
            }

            const inlineNodes = parseInlineFormatting(content);
            const quoteParagraph = $createParagraphNode();
            inlineNodes.forEach(node => quoteParagraph.append(node));
            
            const quoteNode = $createWhatsAppQuoteNode();
            quoteNode.append(quoteParagraph);

            // Append to nodes
            nodes.push(quoteNode);

            i++;
            continue;
        }

        // Regular paragraph
        // Check if line is empty string? If so, it might be just an empty paragraph or spacing.
        // For now, create paragraph.
        const paragraph = $createParagraphNode();
        const inlineNodes = parseInlineFormatting(line);
        inlineNodes.forEach(node => paragraph.append(node));
        nodes.push(paragraph);
        i++;
    }
    
    return nodes;
}

/**
 * Deserialize WhatsApp markdown to Lexical editor nodes
 * 
 * @param markdown - WhatsApp markdown string
 * @param editor - Lexical editor
 */
export const deserializeToEditor = (markdown: string, editor: LexicalEditor): void => {
  editor.update(() => {
    const root = $getRoot();
    root.clear();
    const nodes = $generateNodesFromMarkdown(markdown);
    root.append(...nodes);
  });
};

/**
 * Parse inline formatting in a line of text
 * Handles: *bold*, _italic_, ~strikethrough~, `code`
 */
import {
  IS_BOLD,
  IS_ITALIC,
  IS_STRIKETHROUGH,
  IS_CODE,
} from 'lexical';

/**
 * Parse inline formatting in a line of text recursively
 * Handles: *bold*, _italic_, ~strikethrough~, `code`
 * Supports nesting by using standard TextNodes with format flags.
 */
function parseInlineFormatting(text: string): TextNode[] {
  return parseRecursive(text, 0);
}

function parseRecursive(text: string, currentFormat: number): TextNode[] {
    if (!text) return [];

    // Mentions go FIRST in the priority list: a sequence like
    // `@[Bold *Name*](id@c.us)` should be kept atomic instead of being
    // shredded by the bold matcher inside the brackets.
    const mentionMatch = text.match(MENTION_PATTERN);

    const patterns = [
        { regex: /`([^`\n\r]+)`/, format: IS_CODE, isLiteral: true }, // Code matches first among formatting
        { regex: /\*([^*\n\r]+)\*/, format: IS_BOLD },
        { regex: /_([^_\n\r]+)_/, format: IS_ITALIC },
        { regex: /~([^~\n\r]+)~/, format: IS_STRIKETHROUGH },
    ];

    let bestMatch: { index: number; length: number; text: string; format: number; isLiteral?: boolean } | null = null;
    let bestIsMention = false;
    let mentionData: { display: string; id: string } | null = null;

    if (mentionMatch && mentionMatch.index !== undefined) {
        bestMatch = {
            index: mentionMatch.index,
            length: mentionMatch[0].length,
            text: mentionMatch[0],
            format: 0,
        };
        bestIsMention = true;
        mentionData = { display: mentionMatch[1], id: mentionMatch[2] };
    }

    // Find the earliest match among formatting patterns. A formatting
    // match that comes BEFORE the mention wins; one after loses.
    for (const pattern of patterns) {
        const match = text.match(pattern.regex);
        if (match && match.index !== undefined) {
             if (bestMatch === null || match.index < bestMatch.index) {
                 bestMatch = {
                     index: match.index,
                     length: match[0].length,
                     text: match[1],
                     format: pattern.format,
                     isLiteral: pattern.isLiteral
                 };
                 bestIsMention = false;
                 mentionData = null;
             }
        }
    }

    if (!bestMatch) {
        // No formatting found, return plain text with current format
        const node = $createTextNode(text);
        node.setFormat(currentFormat);
        return [node];
    }

    const nodes: TextNode[] = [];

    // 1. Process text before match
    if (bestMatch.index > 0) {
        const preText = text.substring(0, bestMatch.index);
        const preNodes = parseRecursive(preText, currentFormat);
        nodes.push(...preNodes);
    }

    // 2. Process matched content
    if (bestIsMention && mentionData) {
        // Mentions are atomic — no recursion into the display name.
        const node = $createMentionNode(mentionData);
        // Don't inherit format flags onto a chip; mentions are styled
        // by their own CSS class, mixing in bold/italic would look odd
        // and isn't representable in the WA on-wire form anyway.
        nodes.push(node);
    } else {
        let innerFormat = currentFormat | bestMatch.format;

        if (bestMatch.isLiteral) {
            // Inline code content is literal, do not recurse
            const codeNode = $createTextNode(bestMatch.text);
            codeNode.setFormat(innerFormat);
            nodes.push(codeNode);
        } else {
            // Recurse inner content
            const innerNodes = parseRecursive(bestMatch.text, innerFormat);
            nodes.push(...innerNodes);
        }
    }

    // 3. Process text after match
    const postTextStart = bestMatch.index + bestMatch.length;
    if (postTextStart < text.length) {
        const postText = text.substring(postTextStart);
        const postNodes = parseRecursive(postText, currentFormat);
        nodes.push(...postNodes);
    }

    return nodes;
}
